import { createTheme } from "@dash/default-theme";
import { Theme, ThemeProvider } from "@emotion/react";
import { useConfig } from "@megarax/dev-config";
import { appLinkTreeToNavEntries, MegaraxWebappContainer } from "@megarax/features";
import { filterLinkTree } from "@megarax/home";
import { BlikChecksHome } from "@megarax/loyalty-blik-checks-admin";
import { CartPage, CartsHome } from "@megarax/loyalty-carts-admin";
import { AccountPage, AccountsHome } from "@megarax/loyalty-cash-rewards-accounts-admin";
import { ListingsPage } from "@megarax/loyalty-listings-admin";
import { OrderFulfillmentHome, OrderPackingPage } from "@megarax/loyalty-order-fulfillment";
import { OrderPage, OrdersHome } from "@megarax/loyalty-orders-admin";
import {
  BatchesHome,
  CodeGroupsHome,
  InspectProductCodeView,
  LabelBatchView,
  ProductionBatchView,
  UngroupCodeView,
} from "@megarax/loyalty-product-code-admin";
import { LinePage, QrSyncHome } from "@megarax/loyalty-qr-sync";
import { JSX } from "react";
import { Outlet, Route } from "react-router-dom-v5-compat";

import { LinkDoc } from "@megaron/docs-contracts";

import backgroundImage from "./background_image.png";
import { LoyaltyAdminLogo } from "./LoyaltyAdminLogo";

const loyaltyTheme: Theme = createTheme("328");

type Props = {
  linkDocs: LinkDoc[] | undefined;
};

export const useLoyaltyRouter = ({ linkDocs }: Props): JSX.Element => {
  return (
    <Route
      path="/specjalista"
      element={
        <ThemeProvider theme={loyaltyTheme}>
          <LoyaltyMegaraxWebappContainer linkDocs={linkDocs} />
        </ThemeProvider>
      }
    >
      <Route path="orders">
        <Route path="id/:orderId/*" element={<OrderPage />} />
        <Route path="" element={<OrdersHome />} />
      </Route>
      <Route path="product-codes/groups/*" element={<CodeGroupsHome />} />
      <Route path="product-codes/inspect-code" element={<InspectProductCodeView />} />
      <Route path="product-codes/inspect-code/:codeId" element={<InspectProductCodeView />} />
      <Route path="">
        <Route path="batches/*" element={<BatchesHome />} />
        <Route path="batches/ungroup-code" element={<UngroupCodeView />} />
        <Route path="batches/label-batch/:groupName" element={<LabelBatchView />} />
        <Route path="batches/production-batch/:groupName" element={<ProductionBatchView />} />
      </Route>
      <Route path="listings/*" element={<ListingsPage />} />
      <Route path="qr-sync">
        <Route path="" element={<QrSyncHome />} />
        <Route path="line/:lineId" element={<LinePage />} />
      </Route>
      <Route path="order-fulfillment">
        <Route path="" element={<OrderFulfillmentHome />} />
        <Route path="id/:orderId" element={<OrderPackingPage />} />
      </Route>
      <Route path="carts">
        <Route path="" element={<CartsHome />} />
        <Route path="id/:cartId" element={<CartPage />} />
      </Route>
      <Route path="blik-checks">
        <Route path="" element={<BlikChecksHome />} />
      </Route>
      <Route path="accounts">
        <Route path="id/:userId" element={<AccountPage />} />
        <Route path="" element={<AccountsHome />} />
      </Route>
    </Route>
  );
};

const LoyaltyMegaraxWebappContainer: React.FC<{
  linkDocs: LinkDoc[] | undefined;
}> = ({ linkDocs }) => {
  const fullLinkTree = filterLinkTree(linkDocs || []);
  const appLinkTree = fullLinkTree.find((appTree) => appTree.app === "loyaltyAdmin");

  const { config } = useConfig();

  return (
    <MegaraxWebappContainer
      backgroundImageSrc={backgroundImage}
      logo={<LoyaltyAdminLogo width="1em" height="1em" />}
      title="Specjalista"
      subtitle="megarax.net"
      bypassAuth={config.testUser.enabled}
      navEntries={appLinkTreeToNavEntries(appLinkTree)}
    >
      <Outlet />
    </MegaraxWebappContainer>
  );
};
